import { translateBackend } from '@assets/i18n/config';
import { Link } from '@ovComponents/1-primative/link/link';
import { Badge } from '@ovComponents/2-component/badge/badge';
import type { ColumnDef, Row } from '@ovComponents/2-component/dataTable';
import {
  createDataTableSelectColumn,
  type DataTableColumnMeta,
} from '@ovComponents/2-component/dataTable';
import { Link as RouterLink } from '@ovComponents/2-component/orgLink/orgLink';
import { currencySymbol, isCurrencyCode } from '@utils/currency';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { Theme } from 'hephaestus/core';
import { IconButton, Typography } from 'hephaestus/web';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from 'hephaestus/webIcons';
import type { TFunction } from 'i18next';
import type { MouseEvent } from 'react';
import { processDateAgingDays } from '../utils/activities.config';
import type { ActivitiesGridActivity } from '../utils/activities.queries';
import { reconcileMethodLabel } from '../utils/reconcileMethodLabel';

type BuildActivitiesColumnsOptions = {
  enableBulkSelection: boolean;
  expandedActivityIds: ReadonlySet<string>;
  onToggleExpand: (activityId: string) => void;
};

export const buildActivitiesColumns = (
  t: TFunction,
  theme: Theme,
  {
    enableBulkSelection,
    expandedActivityIds,
    onToggleExpand,
  }: BuildActivitiesColumnsOptions,
): ColumnDef<ActivitiesGridActivity, unknown>[] => {
  const headDefaults: DataTableColumnMeta = {
    headerLabelVariant: 'bodySmall',
    headerLabelWeight: 'bold',
    headerLabelSx: {
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    headerTextColor: theme.color.onSurfaceVariant,
  };

  const dt = (
    partial: Partial<DataTableColumnMeta> = {},
  ): { dataTable: DataTableColumnMeta } => ({
    dataTable: {
      ...headDefaults,
      disableDataTableSortHandling: true,
      ...partial,
    },
  });

  const columns: ColumnDef<ActivitiesGridActivity, unknown>[] = [];

  if (enableBulkSelection) {
    columns.push(
      createDataTableSelectColumn<ActivitiesGridActivity>({
        testIdPrefix: 'recon-activities',
        enableSelectAll: true,
        checkboxSize: 'medium',
      }),
    );
  }

  columns.push(
    {
      id: 'expand',
      header: () => null,
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) => {
        const id = row.original.id;
        const expanded = expandedActivityIds.has(id);
        return (
          <IconButton
            size='small'
            aria-label={expanded ? 'collapse' : 'expand'}
            data-testid={`activities-expand-${id}`}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onToggleExpand(id);
            }}
          >
            {expanded ? (
              <KeyboardArrowDownIcon fontSize='small' />
            ) : (
              <KeyboardArrowRightIcon fontSize='small' />
            )}
          </IconButton>
        );
      },
      meta: dt({}),
    },
    {
      id: 'client',
      header: () => t('activitiesV3.table.client'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) => {
        const activity = row.original;
        const userId = activity.account?.user?.id;
        const label = activity.clientName?.trim() || '—';
        if (!userId) {
          return (
            <Typography variant='bodyMedium' component='span'>
              {label}
            </Typography>
          );
        }
        return (
          <Link
            component={RouterLink}
            to={`/clients/${userId}`}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
            }}
            underline='none'
            sx={{
              color: theme.color.onSurface,
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {label}
          </Link>
        );
      },
      meta: dt({}),
    },
    {
      id: 'organization',
      header: () => t('activitiesV3.table.organization'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        translateBackend(row.original.account?.organization?.translatedName) ||
        '—',
      meta: dt({}),
    },
    {
      id: 'processDate',
      header: () => t('activitiesV3.table.date'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        row.original.processDate || '—',
      meta: dt({}),
    },
    {
      id: 'transactionType',
      header: () => t('activitiesV3.table.type'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) => (
        <Badge label={row.original.transactionType} />
      ),
      meta: dt({}),
    },
    {
      id: 'custodianAccountNumber',
      header: () => t('activitiesV3.table.custodianAcct'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        row.original.custodianAccountNumber ||
        row.original.account?.custodianAccountNumber ||
        '—',
      meta: dt({}),
    },
    {
      id: 'amount',
      header: () => t('activitiesV3.table.amount'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) => {
        const cents = row.original.amountValueCents;
        if (cents == null) {
          return '—';
        }
        const currency = row.original.currency;
        const symbol = isCurrencyCode(currency)
          ? (currencySymbol[currency] ?? '$')
          : '$';
        return formatMoneyValue(cents, symbol, 2, true, true);
      },
      meta: dt({}),
    },
    {
      id: 'accountType',
      header: () => t('activitiesV3.table.acctType'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        row.original.account?.type || '—',
      meta: dt({}),
    },
    {
      id: 'ticker',
      header: () => t('activitiesV3.table.ticker'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        row.original.symbol || '—',
      meta: dt({}),
    },
    {
      id: 'state',
      header: () => t('activitiesV3.table.state'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        t(`activityState.${row.original.state}`, {
          defaultValue: row.original.state,
        }),
      meta: dt({}),
    },
    {
      id: 'aging',
      header: () => t('activitiesV3.table.aging'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        processDateAgingDays(row.original.processDate),
      meta: dt({}),
    },
    {
      id: 'reconciledVia',
      header: () => t('activitiesV3.table.reconciledVia'),
      cell: ({ row }: { row: Row<ActivitiesGridActivity> }) =>
        reconcileMethodLabel(row.original.reconcileMethod),
      meta: dt({}),
    },
  );

  return columns;
};
